import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MuiButton from '@material-ui/core/Button';
import MuiIconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import { fade, darken } from '@material-ui/core/styles/colorManipulator';
import { getBorderRadius } from 'Components/utils';
import { capitalize } from '@material-ui/core';
import {
  buildRootSizeStyles,
  buildSizeObj,
  DS_SIZE_MAP,
  matchRootSize,
  getSizeNames,
} from 'Components/ComponentLibrary/DesignSystem/sizes';
import { dataAttrs } from 'Helpers/Testing';

const darkenOnHover = (color) => darken(color, 0.2);

const MUI_COLORS = ['default', 'inherit', 'primary', 'secondary'];

export const COLORS = [
  ...MUI_COLORS,
  'accentBlue',
  'accentBlue2',
  'accentBlueMuted2',
  'accentBlue10',
  'accentBlue19',
  'accentBlue21',
  'completeGreen',
  'lightGray',
  'mediumDarkGray',
  'primaryGray',
  'actionActive',
  'blueGray3',
  'red',
  'reviewPurple',
  'alertRed',
  'white',
  'seafoam',
  'accentGreen',
  'orange',
  'translucent',
  'frosted',
  'primaryBlue',
  'naturalGreen',
];

const SIZES = [
  buildSizeObj(DS_SIZE_MAP.xs, {}),
  buildSizeObj(DS_SIZE_MAP.sm, {
    minWidth: 200,
    maxWidth: 400,
  }),
  buildSizeObj(DS_SIZE_MAP.md, {
    minWidth: 300,
    maxWidth: 500,
  }),
  buildSizeObj(DS_SIZE_MAP.lg, {
    minWidth: 400,
    maxWidth: 600,
  }),
  buildSizeObj(DS_SIZE_MAP.default, {}),
];

const SIZE_NAMES = getSizeNames(SIZES);

const getVariantFadedContainedColor = (color) => {
  return {
    backgroundColor: fade(color, 0.1),
    color,
    '&:hover': {
      color,
      textDecoration: 'none',
      cursor: 'pointer',
      backgroundColor: fade(color, 0.3),
    },
  };
};

const reduceVariantFadedContainedColors = (theme) => (acc, curr) => {
  const className = `variantFadedContained${capitalize(curr)}`;

  if (theme.palette.facet[curr]) {
    acc[className] = getVariantFadedContainedColor(theme.palette.facet[curr]);
  } else {
    acc[className] = {};
  }

  return acc;
};

const styles = theme => ({
  root: {
    '&:focus': {
      textDecoration: 'none',
    },
  },
  rootRound: {
    ...getBorderRadius(theme.radius.lg, { all: true }),
  },
  ...buildRootSizeStyles(SIZES, (sizeConfig) => {
    return {
      minWidth: sizeConfig.minWidth,
      maxWidth: sizeConfig.maxWidth,
    };
  }),
  paddingDense: {
    padding: theme.spacing(0.5, 1),
    minWidth: 0,
  },
  paddingNone: {
    minWidth: 0,
    padding: 0,
  },
  paddingSquished: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  paddingTight: {
    padding: theme.spacing(0.5),
    minWidth: 0,
  },
  paddingNormal: {
    padding: theme.spacing(1),
    minWidth: 0,
  },
  paddingThick: {
    padding: theme.spacing(2),
  },
  // Contained
  variantContained: {
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.common.white, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantContainedAccentBlue: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlue),
    },
  },
  variantContainedAccentBlue10: {
    backgroundColor: theme.palette.facet.accentBlue10,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.primary.main,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlue10),
    },
  },
  variantContainedPrimaryBlue: {
    backgroundColor: theme.palette.facet.primaryBlue,
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.facet.primaryBlue,
      backgroundColor: darkenOnHover(theme.palette.facet.primaryBlue),
    },
  },
  variantContainedAccentBlue2: {
    backgroundColor: theme.palette.facet.accentBlue2,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlue2),
    },
  },
  variantContainedAccentBlueMuted2: {
    backgroundColor: theme.palette.facet.accentBlueMuted2,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlueMuted2),
    },
  },
  variantContainedAccentBlue19: {
    backgroundColor: theme.palette.facet.accentBlue19,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlue19),
    },
  },
  variantContainedAccentBlue21: {
    backgroundColor: theme.palette.facet.accentBlue21,
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.facet.primaryBlue,
      backgroundColor: darkenOnHover(theme.palette.facet.accentBlue21),
    },
  },
  variantContainedLightGray: {
    backgroundColor: theme.palette.facet.lightGray,
    color: theme.palette.facet.primaryGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.primaryGray,
      cursor: 'pointer',
      backgroundColor: darkenOnHover(theme.palette.facet.lightGray),
    },
  },
  variantContainedMediumDarkGray: {
    backgroundColor: theme.palette.facet.mediumDarkGray,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.common.white,
      cursor: 'pointer',
      backgroundColor: darkenOnHover(theme.palette.facet.mediumDarkGray),
    },
  },
  variantContainedPrimaryGray: {
    backgroundColor: theme.palette.facet.primaryGray,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      backgroundColor: darkenOnHover(theme.palette.facet.primaryGray),
    },
  },
  variantContainedActionActive: {
    backgroundColor: theme.palette.facet.actionActive,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      backgroundColor: darkenOnHover(theme.palette.facet.actionActive),
    },
  },
  variantBlueGray3: {
    backgroundColor: theme.palette.facet.blueGray3,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.primary.main,
      backgroundColor: darkenOnHover(theme.palette.facet.blueGray3),
    },
  },
  variantContainedRed: {
    backgroundColor: theme.palette.facet.red,
    color: theme.palette.common.white,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
      backgroundColor: darkenOnHover(theme.palette.facet.red),
    },
  },
  variantContainedAlertRed: {
    backgroundColor: theme.palette.facet.alertRed,
    color: theme.palette.common.white,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
      backgroundColor: darkenOnHover(theme.palette.facet.alertRed),
    },
  },
  variantContainedOrange: {
    backgroundColor: theme.palette.facet.orange,
    color: theme.palette.common.white,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'none',
      backgroundColor: darkenOnHover(theme.palette.facet.orange),
    },
  },
  variantContainedWhite: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      backgroundColor: darkenOnHover(theme.palette.common.white),
    },
  },
  variantContainedTranslucent: {
    backgroundColor: theme.palette.facet.translucent,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: fade(theme.palette.facet.translucent, 0.2),
    },
  },
  variantContainedSeafoam: {
    backgroundColor: theme.palette.facet.seafoam,
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.facet.seafoam, 0.8),
    },
  },
  variantContainedFrosted: {
    backgroundColor: theme.palette.facet.frosted,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: fade(theme.palette.facet.frosted, 0.6),
    },
  },
  variantContainedAccentGreen: {
    backgroundColor: theme.palette.facet.accentGreen,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: fade(theme.palette.facet.accentGreen, 0.6),
    },
  },
  variantContainedNaturalGreen: {
    backgroundColor: theme.palette.facet.naturalGreen,
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: theme.palette.common.white,
      backgroundColor: fade(theme.palette.facet.naturalGreen, 0.6),
    },
  },
  // Outlined
  variantOutlinedPrimary: {
    color: theme.palette.primary.main,
    border: `1px solid ${fade(theme.palette.primary.main, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: fade(
        theme.palette.primary.main,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedSecondary: {
    color: theme.palette.secondary.main,
    border: `1px solid ${fade(theme.palette.secondary.main, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.secondary.main}`,
      backgroundColor: fade(
        theme.palette.secondary.main,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue: {
    color: theme.palette.facet.accentBlue,
    border: `1px solid ${fade(theme.palette.facet.accentBlue, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue2: {
    color: theme.palette.facet.accentBlue2,
    border: `1px solid ${fade(theme.palette.facet.accentBlue2, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue2}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue2,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedSeafoam: {
    color: theme.palette.facet.seafoam,
    border: `1px solid ${fade(theme.palette.facet.seafoam, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.seafoam}`,
      backgroundColor: fade(
        theme.palette.facet.seafoam,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue10: {
    color: theme.palette.facet.accentBlue10,
    border: `1px solid ${fade(theme.palette.facet.accentBlue10, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue10}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue10,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentBlue21: {
    color: theme.palette.facet.accentBlue21,
    border: `1px solid ${fade(theme.palette.facet.accentBlue21, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.accentBlue21}`,
      backgroundColor: fade(
        theme.palette.facet.accentBlue21,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedLightGray: {
    color: theme.palette.facet.lightGray,
    border: `1px solid ${fade(theme.palette.facet.lightGray, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.lightGray}`,
      backgroundColor: fade(
        theme.palette.facet.lightGray,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedMediumDarkGray: {
    color: theme.palette.facet.mediumDarkGray,
    border: `1px solid ${fade(theme.palette.facet.mediumDarkGray, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.mediumDarkGray}`,
      backgroundColor: fade(
        theme.palette.facet.mediumDarkGray,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedPrimaryGray: {
    color: theme.palette.facet.primaryGray,
    border: `1px solid ${fade(theme.palette.facet.primaryGray, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.primaryGray}`,
      backgroundColor: fade(
        theme.palette.facet.primaryGray,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedActionActive: {
    color: theme.palette.facet.actionActive,
    border: `1px solid ${fade(theme.palette.facet.actionActive, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.actionActive}`,
      backgroundColor: fade(
        theme.palette.facet.actionActive,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedRed: {
    color: theme.palette.facet.red,
    border: `1px solid ${fade(theme.palette.facet.red, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.red}`,
      backgroundColor: fade(
        theme.palette.facet.red,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAlertRed: {
    color: theme.palette.facet.alertRed,
    border: `1px solid ${fade(theme.palette.facet.alertRed, 0.5)}`,
    '&:hover': {
      textDecoration: 'none',
      border: `1px solid ${theme.palette.facet.alertRed}`,
      backgroundColor: fade(
        theme.palette.facet.alertRed,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedWhite: {
    color: theme.palette.common.white,
    border: `1px solid ${fade(theme.palette.common.white, 0.5)}`,
    '&:hover': {
      border: `1px solid ${theme.palette.common.white}`,
      backgroundColor: fade(
        theme.palette.common.white,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedAccentGreen: {
    color: theme.palette.facet.accentGreen,
    border: `1px solid ${fade(theme.palette.facet.accentGreen, 0.5)}`,
    '&:hover': {
      border: `1px solid ${theme.palette.facet.accentGreen}`,
      backgroundColor: fade(
        theme.palette.facet.accentGreen,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  variantOutlinedNaturalGreen: {
    color: theme.palette.facet.naturalGreen,
    border: `1px solid ${fade(theme.palette.facet.naturalGreen, 0.5)}`,
    '&:hover': {
      border: `1px solid ${theme.palette.facet.naturalGreen}`,
      backgroundColor: fade(
        theme.palette.facet.naturalGreen,
        theme.palette.action.hoverOpacity,
      ),
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
  },
  // Text
  variantTextPrimary: {
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.primary.main, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantTextSecondary: {
    color: theme.palette.secondary.main,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.secondary.main, // This needs to stay until a:hover overrides have been removed.
    },
  },
  variantTextAccentBlue: {
    color: theme.palette.facet.accentBlue,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue,
    },
  },
  variantTextAccentBlue2: {
    color: theme.palette.facet.accentBlue2,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue2,
    },
  },
  variantTextAccentBlue10: {
    color: theme.palette.facet.accentBlue10,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue10,
    },
  },
  variantTextAccentBlue21: {
    color: theme.palette.facet.accentBlue21,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentBlue21,
    },
  },
  variantTextPrimaryBlue: {
    color: theme.palette.facet.primaryBlue,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.primaryBlue,
    },
  },
  variantTextLightGray: {
    color: theme.palette.facet.lightGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.lightGray,
    },
  },
  variantTextMediumDarkGray: {
    color: theme.palette.facet.mediumDarkGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.mediumDarkGray,
    },
  },
  variantTextPrimaryGray: {
    color: theme.palette.facet.primaryGray,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.primaryGray,
    },
  },
  variantTextActionActive: {
    color: theme.palette.facet.actionActive,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.actionActive,
    },
  },
  variantTextRed: {
    color: theme.palette.facet.red,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.red,
    },
  },
  variantTextReviewPurple: {
    color: theme.palette.facet.reviewPurple,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.reviewPurple,
    },
  },
  variantTextCompleteGreen: {
    color: theme.palette.facet.completeGreen,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.completeGreen,
    },
  },
  variantTextAlertRed: {
    color: theme.palette.facet.alertRed,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.alertRed,
    },
  },
  variantTextOrange: {
    color: theme.palette.facet.orange,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.orange,
    },
  },
  variantTextSeafoam: {
    color: theme.palette.facet.seafoam,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.seafoam,
    },
  },
  variantTextWhite: {
    color: theme.palette.common.white,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.common.white,
    },
  },
  variantTextAccentGreen: {
    color: theme.palette.facet.accentGreen,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.accentGreen,
    },
  },
  variantTextNaturalGreen: {
    color: theme.palette.facet.naturalGreen,
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.facet.naturalGreen,
    },
  },
  // Underlined
  variantUnderlined: {
    textDecoration: 'underline',
    '&:focus': {
      textDecoration: 'underline',
    },
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  ...COLORS.reduce(reduceVariantFadedContainedColors(theme), {}),
});

const Button = React.forwardRef(
  ({
    classes,
    className,
    color,
    buttonType,
    onClick,
    padding,
    round,
    size,
    variant,
    test,
    ...rest
  }, ref) => {
    const paddingClass = classNames({
      [classes.paddingDense]: padding === 'dense',
      [classes.paddingNone]: padding === 'none',
      [classes.paddingSquished]: padding === 'squished',
      [classes.paddingTight]: padding === 'tight',
      [classes.paddingThick]: padding === 'thick',
      [classes.paddingNormal]: padding === 'normal',
    });
    const isWorkingRef = useRef(false);

    const hasTextStyling = variant === 'text' || variant === 'underlined' || variant === 'icon';


    const variantClass = classNames({
      [classes.variantContained]: variant === 'contained',
      [classes.variantContainedAccentBlue]:
        variant === 'contained' && color === 'accentBlue',
      [classes.variantContainedAccentBlue2]:
        variant === 'contained' && color === 'accentBlue2',
      [classes.variantContainedAccentBlueMuted2]:
      variant === 'contained' && color === 'accentBlueMuted2',
      [classes.variantContainedAccentBlue10]:
        variant === 'contained' && color === 'accentBlue10',
      [classes.variantContainedAccentBlue21]:
        variant === 'contained' && color === 'accentBlue21',
      [classes.variantContainedAccentBlue19]:
        variant === 'contained' && color === 'accentBlue19',
      [classes.variantContainedPrimaryBlue]:
        variant === 'contained' && color === 'primaryBlue',
      [classes.variantContainedLightGray]:
        variant === 'contained' && color === 'lightGray',
      [classes.variantContainedMediumDarkGray]:
        variant === 'contained' && color === 'mediumDarkGray',
      [classes.variantContainedPrimaryGray]:
        variant === 'contained' && color === 'primaryGray',
      [classes.variantContainedActionActive]:
      variant === 'contained' && color === 'actionActive',
      [classes.variantBlueGray3]:
        variant === 'contained' && color === 'blueGray3',
      [classes.variantContainedRed]: variant === 'contained' && color === 'red',
      [classes.variantContainedOrange]:
        variant === 'contained' && color === 'orange',
      [classes.variantContainedSeafoam]:
        variant === 'contained' && color === 'seafoam',
      [classes.variantContainedWhite]:
        variant === 'contained' && color === 'white',
      [classes.variantContainedTranslucent]: variant === 'contained' && color === 'translucent',
      [classes.variantContainedFrosted]: variant === 'contained' && color === 'frosted',
      [classes.variantContainedAccentGreen]: variant === 'contained' && color === 'accentGreen',
      [classes.variantContainedNaturalGreen]: variant === 'contained' && color === 'naturalGreen',
      // Variant Text
      [classes.variantUnderlined]: variant === 'underlined',
      [classes.variantTextPrimary]: hasTextStyling && color === 'primary',
      [classes.variantTextSecondary]: hasTextStyling && color === 'secondary',
      [classes.variantTextAccentBlue]: hasTextStyling && color === 'accentBlue',
      [classes.variantTextAccentBlue2]:
        hasTextStyling && color === 'accentBlue2',
      [classes.variantTextAccentBlue10]:
        hasTextStyling && color === 'accentBlue10',
      [classes.variantTextAccentBlue21]:
        hasTextStyling && color === 'accentBlue21',
      [classes.variantTextPrimaryBlue]:
        hasTextStyling && color === 'primaryBlue',
      [classes.variantTextLightGray]: hasTextStyling && color === 'lightGray',
      [classes.variantTextMediumDarkGray]: hasTextStyling && color === 'mediumDarkGray',
      [classes.variantTextPrimaryGray]:
        hasTextStyling && color === 'primaryGray',
      [classes.variantTextActionActive]:
      hasTextStyling && color === 'actionActive',
      [classes.variantTextRed]: hasTextStyling && color === 'red',
      [classes.variantTextReviewPurple]: variant === 'text' && color === 'reviewPurple',
      [classes.variantTextCompleteGreen]: variant === 'text' && color === 'completeGreen',
      [classes.variantTextOrange]: hasTextStyling && color === 'orange',
      [classes.variantTextSeafoam]: hasTextStyling && color === 'seafoam',
      [classes.variantTextWhite]: hasTextStyling && color === 'white',
      [classes.variantTextAccentGreen]: hasTextStyling && color === 'accentGreen',
      [classes.variantTextNaturalGreen]: hasTextStyling && color === 'naturalGreen',
      // Variant Outlined
      [classes.variantOutlinedAccentBlue]:
        variant === 'outlined' && color === 'accentBlue',
      [classes.variantOutlinedAccentBlue2]:
        variant === 'outlined' && color === 'accentBlue2',
      [classes.variantOutlinedAccentBlue10]:
        variant === 'outlined' && color === 'accentBlue10',
      [classes.variantOutlinedSeafoam]:
        variant === 'outlined' && color === 'seafoam',
      [classes.variantOutlinedAccentBlue21]:
        variant === 'outlined' && color === 'accentBlue21',
      [classes.variantOutlinedLightGray]:
        variant === 'outlined' && color === 'lightGray',
      [classes.variantOutlinedMediumDarkGray]:
        variant === 'outlined' && color === 'mediumDarkGray',
      [classes.variantOutlinedPrimaryGray]:
        variant === 'outlined' && color === 'primaryGray',
      [classes.variantOutlinedActionActive]:
      variant === 'outlined' && color === 'actionActive',
      [classes.variantOutlinedRed]: variant === 'outlined' && color === 'red',
      [classes.variantOutlinedWhite]:
        variant === 'outlined' && color === 'white',
      [classes.variantOutlinedAccentGreen]:
      variant === 'outlined' && color === 'accentGreen',
      [classes.variantOutlinedNaturalGreen]:
      variant === 'outlined' && color === 'naturalGreen',
      [classes[`variantFadedContained${capitalize(color)}`]]: variant === 'faded-contained',
    });

    let ButtonComponent;

    let muiVariant;
    switch (variant) {
      case 'underlined':
        muiVariant = 'text';
        break;
      case 'icon':
        muiVariant = null;
        break;
      case 'faded-contained':
        muiVariant = 'contained';
        break;
      default:
        muiVariant = variant;
        break;
    }

    switch (variant) {
      case 'icon':
        ButtonComponent = MuiIconButton;
        break;
      default:
        ButtonComponent = MuiButton;
        break;
    }

    return (
      <ButtonComponent
        {...dataAttrs(test)}
        className={classNames(
          classes.root,
          paddingClass,
          variantClass,
          className,
          {
            [classes.rootRound]: round,
          },
          matchRootSize(classes, SIZES, size),
        )}
        color={MUI_COLORS.includes(color) ? color : 'inherit'}
        ref={ref}
        onClick={async (evt) => {
          if (!onClick) {
            return;
          }

          if (isWorkingRef.current) {
            return;
          }

          isWorkingRef.current = true;

          try {
            await onClick(evt);
          } catch (err) {
            throw err; // Don't get in the way here.
          }

          isWorkingRef.current = false;
        }}
        variant={muiVariant}
        {...rest}
      />
    );
  },
);

Button.defaultProps = {
  className: '',
  color: 'inherit',
  padding: 'default',
  round: false,
  size: DS_SIZE_MAP.default,
  variant: 'text',
  onClick: () => { },
  test: 'button-component',
};

Button.propTypes = {
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
  size: PropTypes.oneOf(SIZE_NAMES),
  onClick: PropTypes.func,
  padding: PropTypes.oneOf([
    'default',
    'none',
    'dense',
    'squished',
    'tight',
    'normal',
    'thick',
  ]),
  test: PropTypes.string,
  round: PropTypes.bool,
  variant: PropTypes.oneOf([
    'icon',
    'text',
    'outlined',
    'contained',
    'faded-contained',
    'underlined', // Exactly the same as text but with an underline
  ]),
};
export default withStyles(styles)(Button);
